import React from 'react';
import { motion } from 'framer-motion';
import './Pricing.css';

const Pricing = () => {
  const pricingTiers = [
    {
      icon: '💼',
      name: 'Essential Presence',
      setupPrice: '£295 – £595',
      monthlyPrice: '£75 – £125',
      monthlyLabel: 'Managed Infrastructure',
      description: 'For service businesses needing a professional online presence that captures enquiries and builds credibility online.',
      features: [
        'Bespoke UI design (no templates)',
        '4–6 core pages',
        'Mobile optimisation',
        'Secure contact form integration',
        'Managed hosting & SSL',
        'Foundational SEO setup',
        'Performance optimisation',
        'Ongoing maintenance & updates'
      ],
      idealFor: 'Ideal for trades, consultants, and local service providers establishing credibility online.',
      popular: false
    },
    {
      icon: '🚀',
      name: 'Growth Platform',
      setupPrice: '£595 – £895',
      monthlyPrice: '£150 – £200',
      monthlyLabel: 'Managed Infrastructure',
      description: 'Designed for businesses looking to streamline enquiries, improve customer flow, and introduce structured digital systems.',
      features: [
        'Everything in Essential',
        'Booking or scheduling system integration',
        'CRM or workflow integration',
        'Analytics dashboard',
        'Conversion-focused optimisation',
        'Ongoing improvements & feature support'
      ],
      idealFor: 'Designed for businesses looking to automate enquiries and improve customer management.',
      popular: true
    },
    {
      icon: '⚡',
      name: 'Custom Systems',
      setupPrice: 'From £1,200+',
      monthlyPrice: 'From £250+',
      monthlyLabel: 'Infrastructure & Development',
      description: 'For businesses requiring custom dashboards, automation systems, database integration, or scalable backend architecture.',
      features: [
        'Custom feature development',
        'Backend database architecture',
        'API integrations',
        'Advanced workflow automation',
        'Scalable system design'
      ],
      idealFor: 'Scoped individually following consultation.',
      popular: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <section className="pricing-section">
      <div className="pricing-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">💷</span>
            INVESTMENT GUIDE
          </div>
          <h2 className="section-title">Transparent Pricing</h2>
          <p className="section-description">
            Every project includes a one-time setup and ongoing managed infrastructure to ensure
            stability, security, and continuous optimisation.
          </p>
          <p className="section-subdescription">
            Final pricing is confirmed following consultation and scope definition.
          </p>
        </motion.div>

        <motion.div
          className="pricing-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={index}
              className={`pricing-card ${tier.popular ? 'popular' : ''}`}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {tier.popular && (
                <div className="popular-badge">
                  Most Popular
                </div>
              )}
              <div className="card-glow"></div>
              <div className="pricing-card-content">
                <div className="pricing-icon">{tier.icon}</div>
                <h3 className="pricing-tier-name">{tier.name}</h3>
                
                <div className="pricing-amounts">
                  <div className="price-item">
                    <span className="price-value monthly">{tier.monthlyPrice} / month</span>
                    <span className="price-label">{tier.monthlyLabel}</span>
                  </div>
                  <div className="price-item setup-item">
                    <span className="price-label">Setup</span>
                    <span className="price-value setup">{tier.setupPrice}</span>
                  </div>
                </div>

                <p className="pricing-description">{tier.description}</p>

                <div className="pricing-features">
                  <h4>Includes:</h4>
                  <ul>
                    {tier.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="feature-check">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="pricing-ideal">{tier.idealFor}</p>

                <button 
                  className="btn-pricing"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  Get Started
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="pricing-agreement"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          <p>
            All platforms operate on a minimum <strong>12-month infrastructure agreement</strong>. This allows us to
            subsidise the initial build while ensuring your website remains secure, maintained, and continuously
            optimised.
          </p>
        </motion.div>

        <motion.div
          className="additional-development"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Additional Development</h3>
          <p>
            Work outside the agreed project scope is quoted separately and billed at <span className="gradient-text">£65 per hour</span>.
          </p>
          <p className="additional-note">
            All additional work is discussed and approved prior to development.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
